import { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Mousetrap from 'mousetrap';
import cogoToast from 'cogo-toast';
import commaNumber from 'comma-number';
import moment from 'moment';
import cn from 'classnames';
import _ from 'lodash';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSortAmountDownAlt, faTimes, faCopy, faCheck, faSpinner } from '@fortawesome/pro-light-svg-icons';

import './Users.scss';

import TagSidebar from '../Tags/TagSidebar';
import AdminScopeEditor from './AdminScopeEditor';
import BanUserModal from './BanUserModal';

import { fetchUserCounts } from '../../api/users';
import { fetchAvailableScopes } from '../../api/admin_scopes';
import { getUsers, deleteUserTag, updateUserInState, updateUserField, disableUserMfa, clearUsers } from '../../actions/userActions';
import { toggleUserBeingTagged, setUsersBeingTagged } from '../../actions/uiActions';
import { copyToClipboard } from '../../helpers/helpers';
import { getPrettyNumber } from '../../helpers/formatting';
import { getSocialIcon } from '../../helpers/social_helpers';
import { getTierDisplay } from '../../helpers/tier_helpers';
import { getUserTypeBadges, getPresetsForUser, isSuperAdmin, getBanEventSummary, getBanHistoryTooltip } from '../../helpers/user_helpers';
import { isTaggingMode, isUserBeingTagged, getActiveTags, isTagTypeHidden } from '../../helpers/ui_helpers';
import UpdateCell from './UpdateCell';
import SearchInput from '../SearchInput/SearchInput';
import ShimmerRow from './Shimmer/ShimmerRow';
import ShimmerStats from './Shimmer/ShimmerStats';

class Users extends Component {
  static propTypes = {
    ui: PropTypes.object.isRequired,
    users: PropTypes.object.isRequired,
    getUsers: PropTypes.func.isRequired,
    deleteUserTag: PropTypes.func.isRequired,
    setUsersBeingTagged: PropTypes.func.isRequired,
    toggleUserBeingTagged: PropTypes.func.isRequired,
    updateUserInState: PropTypes.func.isRequired
  };

  componentDidMount() {
    const params = {
      page: 1,
      limit: 100,
      sortBy: this.state.sortType[0],
      sortOrder: this.state.sortOrder[0],
      ...(isTaggingMode(this.props.ui) && { withTags: true }),
      ...(this.state.selectedUserType && { userType: this.state.selectedUserType })
    };

    this.props.getUsers(params);
    this.fetchUserCounts();
    this.fetchPresets();
    Mousetrap.bind('/', () => setTimeout(() => this.searchRef && this.searchRef.focus(), 100));
    this.scrollToBottomHandler();
  }

  componentWillUnmount() {
    Mousetrap.unbind('/');
    Mousetrap.unbind('p');

    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const prevActiveTags = getActiveTags(prevProps.ui);
    const activeTags = getActiveTags(this.props.ui);

    // Tag selection changed - refetch with new filter
    if (!_.isEqual(prevActiveTags, activeTags)) {
      window.scrollTo(0, 0);

      const params = {
        page: 1,
        limit: 100,
        sortBy: this.state.sortType[0],
        sortOrder: this.state.sortOrder[0],
        withTags: true,
        ...(activeTags.length > 0 && { tagIds: JSON.stringify(activeTags.map(t => t.id)) }),
        ...(this.state.selectedUserType && { userType: this.state.selectedUserType }),
        ...(this.state.searchVal.trim() && { search: this.state.searchVal.trim() })
      };

      this.props.getUsers(params);
      this.setState({ currentPage: 1 });
    }

    // Entering tagging mode - fetch with tags
    if (!isTaggingMode(prevProps.ui) && isTaggingMode(this.props.ui)) {
      const params = {
        page: 1,
        limit: 100,
        sortBy: this.state.sortType[0],
        sortOrder: this.state.sortOrder[0],
        withTags: true
      };
      this.props.getUsers(params);
    }
  }

  handleSearchChange = value => {
    this.setState({ searchVal: value });
    this.debouncedSearch(value);
  };

  handleClearSearch = () => {
    this.setState({ searchVal: '' });

    const activeTags = getActiveTags(this.props.ui);

    const params = {
      page: 1,
      limit: 100,
      sortBy: this.state.sortType[0],
      sortOrder: this.state.sortOrder[0],
      ...(isTaggingMode(this.props.ui) && { withTags: true }),
      ...(activeTags.length > 0 && { tagIds: JSON.stringify(activeTags.map(t => t.id)) }),
      ...(this.state.selectedUserType && { userType: this.state.selectedUserType }),
      ...(this.state.selectedPresets.length > 0 && { presetLabels: JSON.stringify(this.state.selectedPresets) })
    };

    this.props.getUsers(params);
  };

  handleUserTypeChange = value => {
    if (document.activeElement) {
      document.activeElement.blur();
    }

    // Clear users first
    this.props.clearUsers();

    // Scroll to top
    window.scrollTo(0, 0);

    this.setState({ selectedUserType: value, selectedPresets: [], currentPage: 1 });

    const activeTags = getActiveTags(this.props.ui);

    const params = {
      page: 1,
      limit: 100,
      sortBy: this.state.sortType[0],
      sortOrder: this.state.sortOrder[0],
      ...(isTaggingMode(this.props.ui) && { withTags: true }),
      ...(activeTags.length > 0 && { tagIds: JSON.stringify(activeTags.map(t => t.id)) }),
      ...(value && { userType: value }),
      ...(this.state.searchVal.trim() && { search: this.state.searchVal.trim() })
    };

    this.props.getUsers(params);
  };

  handlePresetFilterChange = selectedOptions => {
    const selectedPresets = (selectedOptions || []).map(o => o.value);

    this.props.clearUsers();
    window.scrollTo(0, 0);
    this.setState({ selectedPresets, currentPage: 1 });

    const activeTags = getActiveTags(this.props.ui);

    const params = {
      page: 1,
      limit: 100,
      sortBy: this.state.sortType[0],
      sortOrder: this.state.sortOrder[0],
      ...(isTaggingMode(this.props.ui) && { withTags: true }),
      ...(activeTags.length > 0 && { tagIds: JSON.stringify(activeTags.map(t => t.id)) }),
      userType: 'admins',
      ...(this.state.searchVal.trim() && { search: this.state.searchVal.trim() }),
      ...(selectedPresets.length > 0 && { presetLabels: JSON.stringify(selectedPresets) })
    };

    this.props.getUsers(params);
  };

  debouncedSearch = _.debounce(searchVal => {
    const trimmedSearch = searchVal.trim();
    const { sortType, sortOrder, selectedUserType, selectedPresets } = this.state;
    const activeTags = getActiveTags(this.props.ui);

    const params = {
      page: 1,
      limit: 100,
      sortBy: sortType[0],
      sortOrder: sortOrder[0],
      ...(isTaggingMode(this.props.ui) && { withTags: true }),
      ...(activeTags.length > 0 && { tagIds: JSON.stringify(activeTags.map(t => t.id)) }),
      ...(selectedUserType && { userType: selectedUserType }),
      ...(trimmedSearch && { search: trimmedSearch }),
      ...(selectedPresets.length > 0 && { presetLabels: JSON.stringify(selectedPresets) })
    };

    this.props.getUsers(params).finally(() => {
      this.setState({
        currentPage: 1
      });
    });
  }, 400);

  /**
   * Generic optimistic update handler
   * @param {Object} params
   * @param {Object} params.user - The user object being updated
   * @param {string} params.fieldName - The field being updated (e.g., 'isBanned', 'isArchived')
   * @param {*} params.newValue - The new value to set
   * @param {string} params.errorAction - Action verb for error message (e.g., 'ban', 'archive')
   * @param {Object} [params.additionalData] - Extra fields sent with the update that are not stored on the user
   * @returns {Promise<boolean>} Whether the update saved
   */
  optimisticUpdate = async ({ user, fieldName, newValue, errorAction, additionalData }) => {
    const oldValue = user[fieldName];

    try {
      // Handles optimistic update
      await this.props.updateUserField({
        userId: user.id,
        fieldName,
        newValue,
        oldValue,
        additionalData
      });

      // Close fleeting success checkmark
      setTimeout(() => {
        this.props.updateUserInState(user.id, { _updateSuccess: null });
      }, 1500);
      return true;
    } catch (err) {
      cogoToast.error(`Failed to ${errorAction} ${user.name}. Please try again.`);
      return false;
    }
  };

  fetchUserCounts = async () => {
    try {
      const counts = await fetchUserCounts();
      this.setState({ userCounts: counts });
    } catch (err) {
      console.error('Error fetching user counts:', err);
    }
  };

  fetchPresets = async () => {
    try {
      const data = await fetchAvailableScopes();
      this.setState({ presets: data.presets || [] });
    } catch (err) {
      console.error('Error fetching presets:', err);
    }
  };

  scrollToBottomHandler = () => {
    // Remove old listener if it exists
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
    }

    this.scrollHandler = () => {
      const pixelsFromBottom = document.body.offsetHeight - window.pageYOffset - window.innerHeight;
      const { isLoadingMore, pagination } = this.props.users;
      const hasMore = pagination?.hasMore;

      if (pixelsFromBottom < 800 && !isLoadingMore && hasMore) {
        this.loadNextPage();
      }
    };

    window.addEventListener('scroll', this.scrollHandler);
  };

  loadNextPage = () => {
    const { isLoadingMore } = this.props.users;

    if (isLoadingMore) {
      return;
    }

    const { pagination } = this.props.users;

    if (!pagination?.hasMore) {
      return;
    }

    const { currentPage, searchVal, selectedUserType, selectedPresets, sortType, sortOrder } = this.state;
    const nextPage = currentPage + 1;

    const activeTags = getActiveTags(this.props.ui);

    const params = {
      page: nextPage,
      limit: 100,
      sortBy: sortType[0],
      sortOrder: sortOrder[0],
      ...(isTaggingMode(this.props.ui) && { withTags: true }),
      ...(activeTags.length > 0 && { tagIds: JSON.stringify(activeTags.map(t => t.id)) }),
      ...(searchVal.trim() && { search: searchVal.trim() }),
      ...(selectedUserType && { userType: selectedUserType }),
      ...(selectedPresets.length > 0 && { presetLabels: JSON.stringify(selectedPresets) })
    };

    this.props.getUsers(params).then(() => {
      this.setState({
        currentPage: nextPage
      });
    });
  };

  state = {
    sortType: ['score'],
    sortOrder: ['desc'],
    searchVal: '',
    showingDescriptions: false,
    selectedUserType: null, // null = 'All Users'
    selectedPresets: [],
    userCounts: null,
    copiedEmailId: null,
    copiedUserId: null,
    currentPage: 1,
    scopeEditorUser: null,
    banModalUser: null,
    presets: []
  };

  setSort = (sortType = ['score'], order = ['desc']) => {
    const switchingSort = _.isEqual(order, this.state.sortOrder);
    const newSortOrder = switchingSort ? order.map(a => (a === 'desc' ? 'asc' : 'desc')) : order;

    // Update state
    this.setState({
      sortType,
      sortOrder: newSortOrder,
      currentPage: 1
    });

    const activeTags = getActiveTags(this.props.ui);

    // Fetch sorted data from backend
    const params = {
      page: 1,
      limit: 100,
      sortBy: sortType[0],
      sortOrder: newSortOrder[0],
      ...(isTaggingMode(this.props.ui) && { withTags: true }),
      ...(activeTags.length > 0 && { tagIds: JSON.stringify(activeTags.map(t => t.id)) }),
      ...(this.state.selectedUserType && { userType: this.state.selectedUserType }),
      ...(this.state.searchVal.trim() && { search: this.state.searchVal.trim() }),
      ...(this.state.selectedPresets.length > 0 && { presetLabels: JSON.stringify(this.state.selectedPresets) })
    };

    this.props.getUsers(params);
  };

  getFilteredAndSortedUsers = () => {
    return _.get(this.props.users, 'users', []);
  };

  changeRate = async (user, type) => {
    const newRate = window.prompt('New rate?', user[type] || '');
    const rateVal = parseInt(newRate);

    if (_.isNaN(rateVal)) {
      cogoToast.error('Please enter a valid value');
      return;
    }

    let fieldName, msg;
    switch (type) {
      case 'user_percentage':
        fieldName = 'user_percentage';
        msg = `Change rate for ${user.name} from ${user.user_percentage}% to ${newRate}%?`;
        break;
      case 'agent_percentage':
        fieldName = 'agent_percentage';
        msg = `Change agent rate for ${user.name} from ${user.agent_percentage}% to ${newRate}%?`;
        break;
      default:
        return;
    }

    const confirm = window.confirm(msg);
    if (!confirm) return;

    await this.optimisticUpdate({
      user,
      fieldName,
      newValue: rateVal,
      errorAction: 'update rate for'
    });
  };

  toggleConcierge = async user => {
    await this.optimisticUpdate({
      user,
      fieldName: 'isConcierge',
      newValue: !user.isConcierge,
      errorAction: user.isConcierge ? 'disable concierge for' : 'enable concierge for'
    });
  };

  togglePromoterOverride = async user => {
    await this.optimisticUpdate({
      user,
      fieldName: 'isPromoterOverride',
      newValue: !user.isPromoterOverride,
      errorAction: user.isPromoterOverride ? 'disable chat for' : 'enable chat for'
    });
  };

  toggleCanFeature = async user => {
    await this.optimisticUpdate({
      user,
      fieldName: 'canFeature',
      newValue: !user.canFeature,
      errorAction: user.canFeature ? 'unfeature' : 'feature'
    });
  };

  archiveUser = async user => {
    const confirm = user.isArchived
      ? window.confirm(`Are you sure you want to unarchive user ${user.name}?`)
      : window.confirm(`Are you sure you want to archive user ${user.name}?`);

    if (!confirm) return;

    await this.optimisticUpdate({
      user,
      fieldName: 'isArchived',
      newValue: !user.isArchived,
      errorAction: user.isArchived ? 'unarchive' : 'archive'
    });
  };

  toggleDiscountCodesDisabled = async user => {
    const isDisabling = !user.discountCodesDisabledAt;
    const confirm = window.confirm(
      isDisabling
        ? `Disable creator codes for ${user.name}? Codes brands made for them will stop earning commission and show as expired until you enable them again. Personal-use offer codes are not affected, and no codes are removed from the brands' Shopify stores.`
        : `Enable creator codes for ${user.name} again? The codes this switch turned off will become active.`
    );
    if (!confirm) return;

    await this.optimisticUpdate({
      user,
      fieldName: 'discountCodesDisabledAt',
      newValue: isDisabling ? true : null,
      errorAction: isDisabling ? 'disable codes for' : 'enable codes for'
    });
  };

  disableUserMfa = async user => {
    const label = user.name || user.email || `user ${user.id}`;
    const confirm = window.confirm(
      `Disable two-factor authentication for ${label}? They'll be able to sign in with just their password until they re-enroll. Note this should only be done upon explicit request from the user in the event they lose access to their authenticator app or device.`
    );
    if (!confirm) return;

    try {
      await this.props.disableUserMfa(user.id);
      cogoToast.success(`Disabled MFA for ${label}.`);
    } catch {
      cogoToast.error(`Failed to disable MFA for ${label}. Please try again.`);
    }
  };

  banUser = async user => {
    const banGloballyResp = window.prompt(
      'Do you want to edit a global ban or a chat-only ban? GLOBAL or CHAT',
      user.isBannedFromChat ? 'CHAT' : 'GLOBAL'
    );

    if (!banGloballyResp || !['GLOBAL', 'CHAT'].includes(banGloballyResp))
      return banGloballyResp && cogoToast.error('Invalid response, please type GLOBAL or CHAT');

    const banGlobally = banGloballyResp === 'GLOBAL';
    if (banGlobally && !user.isBanned) return this.setState({ banModalUser: user });

    const confirm = window.confirm(this.getBanConfirmMessage(user, banGlobally));
    if (!confirm) return;

    const fieldToUpdate = banGlobally ? 'isBanned' : 'isBannedFromChat';

    const didUpdate = await this.optimisticUpdate({
      user,
      fieldName: fieldToUpdate,
      newValue: !user[fieldToUpdate],
      errorAction: user[fieldToUpdate] ? 'unban' : 'ban'
    });

    if (didUpdate && banGlobally) this.addBanEventToRow(user, { action: 'unban' });
  };

  banUserWithReason = async ({ banCategory, banNote }) => {
    const user = this.state.banModalUser;

    const didUpdate = await this.optimisticUpdate({
      user,
      fieldName: 'isBanned',
      newValue: true,
      errorAction: 'ban',
      additionalData: { banCategory, banNote }
    });

    if (didUpdate) {
      this.setState({ banModalUser: null });
      this.addBanEventToRow(user, { action: 'ban', category: banCategory, note: banNote || null });
    }
    return didUpdate;
  };

  addBanEventToRow = (user, banEvent) => {
    const newBanEvent = {
      category: null,
      note: null,
      ...banEvent,
      createdAt: new Date().toISOString(),
      adminName: this.props.users.currentUser?.name
    };
    this.props.updateUserInState(user.id, { ban_events: [newBanEvent, ...(user.ban_events || [])] });
  };

  getBanConfirmMessage = (user, banGlobally) => {
    if (!banGlobally) {
      return user.isBannedFromChat
        ? `Are you sure you want to re-enable chat for user ${user.name}?`
        : `Are you sure you want to Ban chat for user ${user.name}?`;
    }
    return `Are you sure you want to re-enable user ${user.name}? We will re-enable monetization on all of their pins and allow them to be found by brands.`;
  };

  copyEmail = user => {
    copyToClipboard(user.email);
    this.setState({ copiedEmailId: user.id });

    setTimeout(() => {
      this.setState({ copiedEmailId: null });
    }, 2000); // Show checkmark feedback for 2 seconds
  };

  copyUserId = user => {
    copyToClipboard(user.id);
    this.setState({ copiedUserId: user.id });

    setTimeout(() => {
      this.setState({ copiedUserId: null });
    }, 2000);
  };

  getSocial = user => {
    const { social_links } = user;
    if (!social_links) return null;

    // For performance we can memoize these
    // if (_.get(this, ['socialTags', user.id])) return _.get(this, ['socialTags', user.id]);

    const links = social_links.split(',');
    const instagramUrl = _.find(links, p => p.includes('instagram'));
    const tiktokUrl = _.find(links, p => p.includes('tiktok'));
    const youtubeUrl = _.find(links, p => p.includes('youtube'));
    const tags = (
      <>
        {instagramUrl && (
          <a target='_blank' rel='noopener noreferrer' href={instagramUrl}>
            <img className='social-icon' src={getSocialIcon('Instagram')} alt='Instagram' />
          </a>
        )}
        {tiktokUrl && (
          <a target='_blank' rel='noopener noreferrer' href={tiktokUrl}>
            <img className='social-icon' src={getSocialIcon('TikTok')} alt='TikTok' />
          </a>
        )}
        {youtubeUrl && (
          <a target='_blank' rel='noopener noreferrer' href={youtubeUrl}>
            <img className='social-icon' src={getSocialIcon('Youtube')} alt='YouTube' />
          </a>
        )}
      </>
    );
    // this.socialTags = this.projectionTags || {};
    // this.socialTags[user.id] = tags;
    return tags;
  };

  getTypeTag = user => {
    const { tier, score, isAdmin, Brand_id, isArchived } = user;
    const tierDisplay = getTierDisplay(tier);
    if (isAdmin) {
      return {
        display: 'ADMIN',
        className: 'admin'
      };
    }

    if (Brand_id) {
      return {
        display: 'BRAND',
        className: 'brand'
      };
    }

    if (isArchived) {
      return {
        display: 'Archived',
        className: 'archived'
      };
    }

    return {
      display: `${tierDisplay} - ${score?.toFixed(0)}`,
      className: tierDisplay.toLowerCase()
    };
  };

  getProjectionTag = user => {
    // For performance we can memoize these
    // if (_.get(this, ['projectionTags', user.id])) return _.get(this, ['projectionTags', user.id]);

    const { perMonthEarnings, thisMonthEarnings } = user;
    const projMonthEarnings = (thisMonthEarnings * 30.5) / moment().date();
    const ratio = projMonthEarnings / perMonthEarnings;
    const growth = 100 * ratio || 0;

    let tag;
    if (growth > 250) {
      tag = { className: 'highest' };
    } else if (growth > 175) {
      tag = { className: 'higher' };
    } else if (growth > 120) {
      tag = { className: 'high' };
    } else if (growth > 80) {
      tag = { className: 'middle' };
    } else if (growth > 60) {
      tag = { className: 'low' };
    } else if (growth > 40) {
      tag = { className: 'lower' };
    } else if (growth > 1) {
      tag = { className: 'lowest' };
    } else {
      tag = { className: 'empty' };
    }

    // this.projectionTags = this.projectionTags || {};
    // this.projectionTags[user.id] = tag;

    return tag;
  };

  getTotalProjections = () => {
    const aggregates = this.props.users.aggregates;

    if (!aggregates) {
      // Fallback if aggregates not loaded yet
      return {
        expected: 0,
        projected: 0,
        expectedCounts: { tiny: 0, small: 0, medium: 0, large: 0, huge: 0 },
        projectedCounts: { tiny: 0, small: 0, medium: 0, large: 0, huge: 0 }
      };
    }

    return {
      expected: aggregates.totalExpected,
      projected: aggregates.totalProjected,
      expectedCounts: aggregates.expectedCounts,
      projectedCounts: aggregates.projectedCounts
    };
  };

  render() {
    const { ui, toggleUserBeingTagged } = this.props;
    const { showingDescriptions, searchVal, scopeEditorUser, banModalUser } = this.state;
    const { isLoading, isLoadingMore, isSearching, isLoadingTags, currentUser } = this.props.users;
    const superAdmin = isSuperAdmin(currentUser);

    const users = this.getFilteredAndSortedUsers();
    const totalProjections = this.getTotalProjections();
    const selectedTagIds = _.map(getActiveTags(this.props.ui), 'id');
    const inTaggingMode = isTaggingMode(ui);
    const showAdminRoleFilter = superAdmin && this.state.selectedUserType === 'admins' && !inTaggingMode;

    return (
      <div className={cn('users-outer-container', { tagging: inTaggingMode })}>
        <TagSidebar />
        <div className='users-inner-container'>
          <div className='header-stats'>
            <div className='action-btns'>
              <SearchInput
                value={searchVal}
                onChange={({ target }) => this.handleSearchChange(target.value)}
                placeholder='Search by name, username, email, bio, id'
                isLoading={isSearching}
                inputRef={ref => (this.searchRef = ref)}
                onClear={this.handleClearSearch}
              />
              <Select
                className='select'
                placeholder='User Type'
                isSearchable={false}
                value={
                  this.state.selectedUserType
                    ? {
                        label:
                          this.state.selectedUserType === 'creators'
                            ? 'Creators'
                            : this.state.selectedUserType === 'shoppers'
                            ? 'Shoppers'
                            : this.state.selectedUserType === 'admins'
                            ? 'Admins'
                            : 'Brands',
                        value: this.state.selectedUserType
                      }
                    : {
                        label: this.state.userCounts ? `All Users (${this.state.userCounts.total})` : 'All Users',
                        value: null
                      }
                }
                onChange={({ value }) => this.handleUserTypeChange(value)}
                options={[
                  {
                    label: this.state.userCounts ? `All Users (${this.state.userCounts.total})` : 'All Users',
                    value: null
                  },
                  {
                    label: this.state.userCounts ? `Creators (${this.state.userCounts.creators})` : 'Creators',
                    value: 'creators'
                  },
                  {
                    label: this.state.userCounts ? `Shoppers (${this.state.userCounts.shoppers})` : 'Shoppers',
                    value: 'shoppers'
                  },
                  {
                    label: this.state.userCounts ? `Brands (${this.state.userCounts.brands})` : 'Brands',
                    value: 'brands'
                  },
                  {
                    label: this.state.userCounts ? `Admins (${this.state.userCounts.admins})` : 'Admins',
                    value: 'admins'
                  }
                ]}
              />
              {showAdminRoleFilter && (
                <Select
                  className='select preset-filter'
                  placeholder='Filter by Role...'
                  isMulti
                  isSearchable={false}
                  value={this.state.selectedPresets.map(label => ({ label, value: label }))}
                  onChange={this.handlePresetFilterChange}
                  options={this.state.presets.map(p => ({ label: p.label, value: p.label }))}
                />
              )}
            </div>
            {!!isLoading && users.length === 0 && <ShimmerStats />}
            {!isLoading && users.length > 0 && !showAdminRoleFilter && (
              <div className='header-stat'>
                <div className='stat'>
                  Expected {totalProjections.expectedCounts.huge},{totalProjections.expectedCounts.large},{totalProjections.expectedCounts.medium},
                  {totalProjections.expectedCounts.small},{totalProjections.expectedCounts.tiny} - Projected {totalProjections.projectedCounts.huge}{' '}
                  HUGE, {totalProjections.projectedCounts.large} LARGE, {totalProjections.projectedCounts.medium} MEDIUM,{' '}
                  {totalProjections.projectedCounts.small} SMALL, {totalProjections.projectedCounts.tiny} TINY
                </div>
                <div className='stat'>
                  Expected ${commaNumber(totalProjections.expected.toFixed(0))} - Projected ${commaNumber(totalProjections.projected.toFixed(0))}
                </div>
              </div>
            )}
          </div>

          <div className='users-table-container'>
            <div className={cn('users-table', { 'is-loading': (isLoading || isSearching) && users.length > 0 })}>
              <div className='user row header'>
                {inTaggingMode ? (
                  <>
                    <div onClick={() => this.props.setUsersBeingTagged(users)} className='cell xs'>
                      Select
                    </div>
                    <div onClick={() => this.setSort(['name'], ['asc'])} className='cell lg has-filter'>
                      Name
                      <FontAwesomeIcon icon={faSortAmountDownAlt}></FontAwesomeIcon>
                    </div>
                    <div className='cell lg has-filter'>Username</div>
                    <div className='cell lg'>Email</div>
                    <div className='cell sm'>User Type</div>
                    {showAdminRoleFilter && <div className='cell md'>Admin Roles</div>}
                    <div className='cell xl'>Tags</div>
                    <div className='cell md'>Social</div>
                    <div className='cell md'>Followers</div>
                    <div onClick={() => this.setSort(['perMonthEarnings'], ['desc'])} className='cell md has-filter'>
                      Per Month
                      <FontAwesomeIcon icon={faSortAmountDownAlt}></FontAwesomeIcon>
                    </div>
                    <div onClick={() => this.setSort(['score'], ['desc'])} className='cell md has-filter'>
                      Tier
                      <FontAwesomeIcon icon={faSortAmountDownAlt}></FontAwesomeIcon>
                    </div>
                    <div onClick={() => this.setSort(['createdAt'], ['desc'])} className='cell md has-filter'>
                      Created On
                      <FontAwesomeIcon icon={faSortAmountDownAlt}></FontAwesomeIcon>
                    </div>
                    <div className='cell xl'>Description</div>
                  </>
                ) : (
                  <>
                    <div onClick={() => this.setSort(['id'], ['asc'])} className='cell sm has-filter'>
                      User ID
                      <FontAwesomeIcon icon={faSortAmountDownAlt}></FontAwesomeIcon>
                    </div>
                    <div onClick={() => this.setSort(['name'], ['asc'])} className='cell lg has-filter'>
                      Name
                      <FontAwesomeIcon icon={faSortAmountDownAlt}></FontAwesomeIcon>
                    </div>
                    <div onClick={() => this.setSort(['username'], ['asc'])} className='cell lg has-filter'>
                      Username
                      <FontAwesomeIcon icon={faSortAmountDownAlt}></FontAwesomeIcon>
                    </div>
                    <div className='cell lg'>Email</div>
                    <div className='cell sm'>User Type</div>
                    {showAdminRoleFilter && <div className='cell md'>Admin Roles</div>}
                    <div className='cell md'>Social</div>
                    <div className='cell md'>Followers</div>
                    <div onClick={() => this.setSort(['perMonthEarnings'], ['desc'])} className='cell md has-filter'>
                      Per Month
                      <FontAwesomeIcon icon={faSortAmountDownAlt}></FontAwesomeIcon>
                    </div>
                    <div onClick={() => this.setSort(['thisMonthEarnings'], ['desc'])} className='cell md has-filter'>
                      This Month
                      <FontAwesomeIcon icon={faSortAmountDownAlt}></FontAwesomeIcon>
                    </div>
                    <div onClick={() => this.setSort(['score'], ['desc'])} className='cell md has-filter'>
                      Tier
                      <FontAwesomeIcon icon={faSortAmountDownAlt}></FontAwesomeIcon>
                    </div>
                    <div className='cell sm'>Concierge</div>
                    <div className='cell sm'>Can Chat</div>
                    <div className='cell sm'>Is Featured</div>
                    <div className='cell xs'>Rate</div>
                    <div onClick={() => this.setSort(['createdAt'], ['desc'])} className='cell md has-filter'>
                      Created On
                      <FontAwesomeIcon icon={faSortAmountDownAlt}></FontAwesomeIcon>
                    </div>
                    <div className='cell sm'>Ban</div>
                    <div className='cell md'>Ban Reason</div>
                    <div className='cell sm'>Codes</div>
                    <div className='cell sm'>Archive</div>
                    {superAdmin && <div className='cell sm'>MFA</div>}
                  </>
                )}
              </div>

              {isLoading && users.length === 0 && (
                <>
                  {[...Array(100)].map((_, idx) => (
                    <ShimmerRow key={`shimmer-${idx}`} inTaggingMode={inTaggingMode} odd={idx % 2 === 1} />
                  ))}
                </>
              )}

              {_.map(users, (user, idx) => {
                const {
                  id,
                  name,
                  username,
                  description,
                  tags,
                  tier,
                  perMonthEarnings,
                  thisMonthEarnings,
                  createdAt,
                  canFeature,
                  user_percentage
                } = user;
                const projTag = this.getProjectionTag(user, idx);
                const typeTag = this.getTypeTag(user);
                const social = this.getSocial(user);
                const visibleTags = _.filter(tags, tag => !isTagTypeHidden(tag.type, ui));
                const canChatDueToTier = tier <= 3;
                const canChat = user.isPromoterOverride || canChatDueToTier;
                const followers = user.instagramCount + user.youtubeCount + user.tiktokCount;
                const latestBanEvent = user.ban_events?.[0];
                return (
                  <div className={cn('users row', { odd: idx % 2 })} key={id}>
                    {inTaggingMode ? (
                      <>
                        <div onClick={() => toggleUserBeingTagged(user)} className='cell xs'>
                          <div className={cn('checkbox', { checked: isUserBeingTagged(user, ui) })}></div>
                        </div>
                        <div className='cell lg'>{name}</div>
                        <a target='_blank' rel='noopener noreferrer' href={`https://shopmy.us/${username}`} className='cell lg'>
                          {username}
                        </a>
                        <div className='cell lg email-cell' onClick={() => this.copyEmail(user)}>
                          <span className='email-text'>{user.email || '-'}</span>
                          <span className={cn('copy-icon', { copied: this.state.copiedEmailId === user.id })}>
                            <FontAwesomeIcon icon={this.state.copiedEmailId === user.id ? faCheck : faCopy} />
                          </span>
                        </div>
                        <div
                          className={cn('cell sm user-type-cell', { clickable: superAdmin })}
                          onClick={() => superAdmin && this.setState({ scopeEditorUser: user })}
                        >
                          {getUserTypeBadges(user).map((badge, idx) => (
                            <div key={idx} className={`badge ${badge.className}`}>
                              {badge.display}
                            </div>
                          ))}
                        </div>
                        {showAdminRoleFilter && (
                          <div className='cell md admin-roles-cell'>
                            {getPresetsForUser(user, this.state.presets).map((label, idx) => (
                              <div key={idx} className='badge preset'>
                                {label}
                              </div>
                            ))}
                          </div>
                        )}
                        <div className='cell xl tags'>
                          {isLoadingTags ? (
                            <div className='shimmer-tags-container'>
                              <div className='shimmer-tag' />
                              <div className='shimmer-tag' />
                              <div className='shimmer-tag' />
                            </div>
                          ) : (
                            _.orderBy(
                              visibleTags,
                              tag => {
                                if (selectedTagIds.includes(tag.id)) return 10;
                                if (tag.type === 'general') return 9;
                                if (tag.type === 'geo') return 8;
                                if (tag.type === 'catalog') return 7;
                                if (tag.type === 'social') return 6;
                                if (tag.type === 'activity') return 5;
                                if (tag.type === 'behavior') return 4;
                                return 0;
                              },
                              'desc'
                            ).map((tag, idx) => {
                              const isSelected = selectedTagIds.includes(tag.id);
                              return (
                                <div
                                  onClick={() => this.props.deleteUserTag(tag, user)}
                                  key={tag.value + idx}
                                  className={cn('tag', tag.type, { selected: isSelected })}
                                >
                                  {tag.value}
                                  <FontAwesomeIcon icon={faTimes} />
                                </div>
                              );
                            })
                          )}
                        </div>
                        <div className='cell md'>{social}</div>
                        <div className='cell md'>{followers ? getPrettyNumber(followers) : '-'}</div>
                        <div className='cell md'>{perMonthEarnings ? `$${perMonthEarnings.toFixed(2)}` : '-'}</div>
                        <div className='cell md'>
                          <div className={`badge ${typeTag.className}`}>{typeTag.display}</div>
                        </div>
                        <div className='cell md'>
                          {createdAt ? moment(createdAt).format(this.state.sortType.includes('createdAt') ? 'MMM Do, YYYY' : 'MMM Do') : '-'}
                        </div>
                        <div
                          className={cn('cell xl description', { 'visible-description': showingDescriptions })}
                          onClick={() => this.setState({ showingDescriptions: !showingDescriptions })}
                        >
                          {description}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className='cell sm id-cell' onClick={() => this.copyUserId(user)}>
                          <span className='id-text'>{id}</span>
                          <span className={cn('copy-icon', { copied: this.state.copiedUserId === user.id })}>
                            <FontAwesomeIcon icon={this.state.copiedUserId === user.id ? faCheck : faCopy} />
                          </span>
                        </div>
                        <div className='cell lg'>{name}</div>
                        <a target='_blank' rel='noopener noreferrer' href={`https://shopmy.us/${username}`} className='cell lg'>
                          {username}
                        </a>
                        <div className='cell lg email-cell' onClick={() => this.copyEmail(user)}>
                          <span className='email-text'>{user.email || '-'}</span>
                          <span className={cn('copy-icon', { copied: this.state.copiedEmailId === user.id })}>
                            <FontAwesomeIcon icon={this.state.copiedEmailId === user.id ? faCheck : faCopy} />
                          </span>
                        </div>
                        <div
                          className={cn('cell sm user-type-cell', { clickable: superAdmin })}
                          onClick={() => superAdmin && this.setState({ scopeEditorUser: user })}
                        >
                          {getUserTypeBadges(user).map((badge, idx) => (
                            <div key={idx} className={`badge ${badge.className}`}>
                              {badge.display}
                            </div>
                          ))}
                        </div>
                        {showAdminRoleFilter && (
                          <div className='cell md admin-roles-cell'>
                            {getPresetsForUser(user, this.state.presets).map((label, idx) => (
                              <div key={idx} className='badge preset'>
                                {label}
                              </div>
                            ))}
                          </div>
                        )}
                        <div className='cell md'>{social}</div>
                        <div className='cell md'>{followers ? getPrettyNumber(followers) : '-'}</div>
                        <div className='cell md'>{perMonthEarnings ? `$${perMonthEarnings.toFixed(2)}` : '-'}</div>
                        <div className={cn('cell md proj', projTag.className)}>{thisMonthEarnings ? `$${thisMonthEarnings.toFixed(2)}` : '-'}</div>
                        <div className='cell md'>
                          <div className={`badge ${typeTag.className}`}>{typeTag.display}</div>
                        </div>
                        <UpdateCell user={user} fieldName='isConcierge' className='sm btn' onClick={() => this.toggleConcierge(user)}>
                          {user.isConcierge ? '✓' : '-'}
                        </UpdateCell>
                        <UpdateCell
                          user={user}
                          fieldName='isPromoterOverride'
                          className='sm btn'
                          onClick={() =>
                            canChatDueToTier ? cogoToast.warn(`This user can already chat due to their tier.`) : this.togglePromoterOverride(user)
                          }
                        >
                          {canChat ? '✓' : '-'}
                        </UpdateCell>
                        <UpdateCell user={user} fieldName='canFeature' className='sm btn' onClick={() => this.toggleCanFeature(user)}>
                          {canFeature ? '✓' : '-'}
                        </UpdateCell>
                        <UpdateCell
                          user={user}
                          fieldName='user_percentage'
                          className='xs btn'
                          onClick={() => this.changeRate(user, 'user_percentage')}
                        >
                          {user_percentage ? `${user_percentage}%` : '-'}
                        </UpdateCell>
                        <div className='cell md'>
                          {createdAt ? moment(createdAt).format(this.state.sortType.includes('createdAt') ? 'MMM Do, YYYY' : 'MMM Do, YYYY') : '-'}
                        </div>
                        <UpdateCell
                          user={user}
                          fieldName={user.isBanned ? 'isBanned' : 'isBannedFromChat'}
                          className='sm btn'
                          onClick={() => this.banUser(user)}
                        >
                          {user.isBanned ? 'Unban' : user.isBannedFromChat ? 'Banned From Chat' : 'Ban'}
                        </UpdateCell>
                        <div
                          className={cn('cell md ban-reason-cell', { 'has-history': !!latestBanEvent })}
                          title={getBanHistoryTooltip(user.ban_events)}
                        >
                          {latestBanEvent ? getBanEventSummary(latestBanEvent) : '-'}
                        </div>
                        <UpdateCell
                          user={user}
                          fieldName='discountCodesDisabledAt'
                          className='sm btn'
                          onClick={() => this.toggleDiscountCodesDisabled(user)}
                        >
                          {user.discountCodesDisabledAt ? 'Enable Codes' : 'Disable Codes'}
                        </UpdateCell>
                        <UpdateCell user={user} fieldName='isArchived' className='sm btn' onClick={() => this.archiveUser(user)}>
                          {user.isArchived ? 'Unarchive' : 'Archive'}
                        </UpdateCell>
                        {superAdmin && (
                          <div className='cell sm mfa-cell'>
                            {user.mfaEnabledAt ? (
                              <span className='mfa-status'>
                                Enabled {moment(user.mfaEnabledAt).format("MMM 'YY")}
                                {user._updating === 'mfaEnabledAt' ? (
                                  <FontAwesomeIcon icon={faSpinner} spin className='mfa-icon' />
                                ) : (
                                  <FontAwesomeIcon
                                    icon={faTimes}
                                    className='mfa-icon remove'
                                    title='Disable MFA'
                                    onClick={() => this.disableUserMfa(user)}
                                  />
                                )}
                              </span>
                            ) : (
                              '-'
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}

              {/* Show shimmer rows at bottom when loading more (pagination) */}
              {isLoadingMore && (
                <>
                  {[...Array(5)].map((_, idx) => (
                    <ShimmerRow key={`shimmer-more-${idx}`} inTaggingMode={inTaggingMode} odd={(users.length + idx) % 2 === 1} />
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
        {superAdmin && scopeEditorUser && (
          <AdminScopeEditor
            user={scopeEditorUser}
            presets={this.state.presets}
            onClose={() => this.setState({ scopeEditorUser: null })}
            onUpdated={() => {
              const activeTags = getActiveTags(this.props.ui);
              const params = {
                page: 1,
                limit: 100,
                sortBy: this.state.sortType[0],
                sortOrder: this.state.sortOrder[0],
                ...(isTaggingMode(this.props.ui) && { withTags: true }),
                ...(activeTags.length > 0 && { tagIds: JSON.stringify(activeTags.map(t => t.id)) }),
                ...(this.state.selectedUserType && { userType: this.state.selectedUserType }),
                ...(this.state.searchVal.trim() && { search: this.state.searchVal.trim() }),
                ...(this.state.selectedPresets.length > 0 && { presetLabels: JSON.stringify(this.state.selectedPresets) })
              };
              this.props.getUsers(params);
            }}
          />
        )}
        {banModalUser && (
          <BanUserModal user={banModalUser} onConfirm={this.banUserWithReason} onClose={() => this.setState({ banModalUser: null })} />
        )}
      </div>
    );
  }
}

const mapStateToProps = state => {
  const { users, ui } = state;
  return { users, ui };
};

export default connect(mapStateToProps, {
  getUsers,
  deleteUserTag,
  setUsersBeingTagged,
  toggleUserBeingTagged,
  updateUserInState,
  updateUserField,
  disableUserMfa,
  clearUsers
})(Users);
